class CollectionBrowseAPILayer {
  constructor () {
    this.eventBus = window.theme.collectionBrowse.eventBus

    this.subscribeToEvents()
  }

  subscribeToEvents () {
    this.eventBus.subscribe(
      'collectionBrowse:api:fetch:products',
      (requestMeta) => {
        this.fetchResults(requestMeta)
      },
    )

    this.eventBus.subscribe(
      'collectionBrowse:tracking:product:clicked',
      (productData) => {
        if (!this.tracker || !productData?.productId) {
          return
        }

        this.tracker.trackBrowseResultClick({
          filterName: 'group_id',
          filterValue: this.collectionHandle,
          itemId: productData.productId,
          itemName: productData.itemName,
          numResultsPerPage: this.resultsPerPage,
          resultCount: this.pagination.total,
          resultId: this.pagination.resultId,
          resultPage: productData.sourcePage || this.pagination.currentPage,
          resultPositionOnPage: productData.resultPositionOnPage,
          selectedFilters: this.stateLayer?.selectedFilters || {},
          variationId: productData.variationId,
        })
      },
    )
  }

  async fetchResults (requestMeta = {}) {
    if (!this.client || !this.collectionHandle) {
      this.publishResults()
      return
    }

    try {
      const filterParameters = await this.buildFilterParameters()
      const sortParameters = this.buildSortParameters()
      const targetPage = requestMeta.targetPage || this.currentPage
      const response = await this.client.browse.getBrowseResults(
        'group_id',
        this.collectionHandle,
        {
          page: targetPage,
          resultsPerPage: this.resultsPerPage,
          section: 'Products',
          fmtOptions: {
            fields: [
              'id',
              'url',
              'name',
              'price',
              'list_price',
              'variation_id',
            ],
          },
          ...filterParameters,
          ...sortParameters,
        },
      )

      this.publishResults(this.mapConstructorResponse(response, requestMeta))
    } catch (error) {
      console.error('Failed to fetch Constructor browse results', error)
      this.publishResults()
    }
  }

  publishResults (response = this.emptyResponse) {
    this.eventBus.publish('collectionBrowse:api:results:fetched', response)
  }

  async buildFilterParameters () {
    const activeFilters = this.getActiveFilterInputs()
    const activeFacets = []
    const priceRange = {
      min: null,
      max: null,
    }

    activeFilters.forEach((filter) => {
      if (filter.type === 'price_range') {
        if (filter.min != null) {
          priceRange.min = filter.min
        }

        if (filter.max != null) {
          priceRange.max = filter.max
        }

        return
      }

      if (!filter.field || filter.value == null || filter.value === '') {
        return
      }

      activeFacets.push({
        field: filter.field,
        value: filter.value,
      })
    })

    if (priceRange.min !== null || priceRange.max !== null) {
      activeFacets.push({
        field: 'price',
        value: `${priceRange.min || '-inf'}-${priceRange.max || 'inf'}`,
      })
    }

    return this.buildSharedFilterParameters(activeFacets)
  }

  buildSharedFilterParameters (activeFacets = []) {
    return window.theme.constructorApiHelpers.buildFilterParameters(
      activeFacets,
    )
  }

  buildSortParameters () {
    if (!this.sortBy || !this.sortOrder) {
      return {}
    }

    return {
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    }
  }

  getActiveFilterInputs () {
    const activeFilters = []
    const seenFilters = new Set()

    const appendFilter = (filter) => {
      if (filter.type === 'price_range') {
        activeFilters.push(filter)
        return
      }

      const filterKey = `${filter.field}::${filter.value}`

      if (!filter.field || !filter.value || seenFilters.has(filterKey)) {
        return
      }

      seenFilters.add(filterKey)
      activeFilters.push(filter)
    }

    document
      .querySelectorAll(
        'form.clc-ProductGridFacets input[name^="filter."], form.clc-ProductGridFacets input[data-cnstrc-field]',
      )
      .forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {
          return
        }

        if (input.name === 'filter.v.price.gte') {
          if (input.value !== '') {
            activeFilters.push({ type: 'price_range', min: input.value })
          }

          return
        }

        if (input.name === 'filter.v.price.lte') {
          if (input.value !== '') {
            activeFilters.push({ type: 'price_range', max: input.value })
          }

          return
        }

        if (input.name === 'filter.v.availability') {
          return
        }

        if (input.type === 'checkbox' && !input.checked) {
          return
        }

        appendFilter({
          type: 'list',
          field: this.getFilterField(input),
          value: this.getFilterParameterValue(input),
        })
      })

    this.searchParams.forEach((value, key) => {
      if (!key.startsWith('filter.')) {
        return
      }

      if (key === 'filter.v.price.gte') {
        if (value !== '') {
          activeFilters.push({ type: 'price_range', min: value })
        }

        return
      }

      if (key === 'filter.v.price.lte') {
        if (value !== '') {
          activeFilters.push({ type: 'price_range', max: value })
        }

        return
      }

      if (key === 'filter.v.availability') {
        return
      }

      appendFilter({
        type: 'list',
        field: this.getFilterFieldFromParamName(key),
        value,
      })
    })

    return activeFilters
  }

  getFilterField (input) {
    if (input.dataset.cnstrcField) {
      return input.dataset.cnstrcField
    }

    const fieldFromParam = this.getFilterFieldFromParamName(input.name)

    if (fieldFromParam) {
      return fieldFromParam
    }

    if (input.name === 'filter.v.availability') {
      return 'availability'
    }

    const facetItem = input.closest('.clc-ProductGridFacets_Item')
    const labelElement =
      facetItem?.querySelector('.clc-ProductGridFacets_FilterLabel') ||
      facetItem?.querySelector('.clc-ProductGridFacet_FilterLabel')

    return this.normalizeLabel(labelElement?.textContent || '')
  }

  getFilterParameterValue (input) {
    if (input.dataset.cnstrcValue) {
      return input.dataset.cnstrcValue
    }

    if (input.name === 'filter.v.availability') {
      return input.value === '1' ? 'True' : 'False'
    }

    return input.value
  }

  normalizeLabel (value) {
    return value.replace(/\s+/g, ' ').trim()
  }

  normalizeBrowseLabel (value) {
    return this.normalizeLabel(value)
  }

  getBrowseFieldLabel (input) {
    if (input.dataset.cnstrcField) {
      return input.dataset.cnstrcField
    }

    const fieldFromParam = this.getFilterFieldFromParamName(input.name)

    if (fieldFromParam) {
      return fieldFromParam
    }

    const facetItem = input.closest('.clc-ProductGridFacets_Item')

    if (!facetItem) {
      return ''
    }

    const labelElement =
      facetItem.querySelector('.clc-ProductGridFacets_FilterLabel') ||
      facetItem.querySelector('.clc-ProductGridFacet_FilterLabel')

    return this.normalizeBrowseLabel(labelElement?.textContent || '')
  }

  getBrowseValueLabel (input) {
    if (input.dataset.cnstrcValue) {
      return input.dataset.cnstrcValue
    }

    const labelElement = input.closest('label')

    if (!labelElement) {
      return ''
    }

    const labelText = labelElement.textContent || ''

    return this.normalizeBrowseLabel(labelText.replace(/\s*\(\d+\)\s*$/, ''))
  }

  getFilterFieldFromParamName (paramName = '') {
    const match = paramName.match(/^filter\.[^.]+\.(.+)$/)

    if (!match) {
      return ''
    }

    const field = match[1]
    const normalizedField = field.split('.').pop() || ''

    if (
      field === 'price.gte' ||
      field === 'price.lte' ||
      field === 'availability'
    ) {
      return ''
    }

    if (normalizedField === 'color-pattern') {
      return 'color'
    }

    return normalizedField
  }

  flattenFacetOptions (options = []) {
    return window.theme.constructorApiHelpers.flattenFacetOptions(options)
  }

  isFacetSelected (field, value) {
    return this.activeFacets.some((facet) => {
      if (facet.field !== field) return false

      return Array.isArray(facet.value)
        ? facet.value.includes(value)
        : facet.value === value
    })
  }

  mapFacet (facet) {
    if (facet?.type === 'range') {
      return {
        field: facet.name,
        max: facet.max,
        min: facet.min,
        name: facet.display_name || facet.name,
        type: facet.type,
      }
    }

    const mappedFacet = window.theme.constructorApiHelpers.mapFacet(
      facet,
      this.activeFacets,
    )

    if (!mappedFacet) {
      return null
    }

    return {
      ...mappedFacet,
      type: facet?.type || 'multiple',
    }
  }

  mapProduct (result = {}) {
    return window.theme.constructorApiHelpers.mapProduct(result)
  }

  getItemPrice (itemData = {}) {
    return window.theme.constructorApiHelpers.getItemPrice(itemData)
  }

  mapConstructorResponse (response = {}, requestMeta = {}) {
    const constructorResponse = response.response || response || {}
    const products = Array.isArray(constructorResponse.results)
      ? constructorResponse.results
        .map((result) => this.mapProduct(result))
        .filter(Boolean)
      : []
    const promotions = window.theme.constructorApiHelpers.mapPromotions(
      constructorResponse.refined_content,
    )
    const hits = window.theme.constructorApiHelpers.buildRenderItems({
      products,
      promotions,
      slotCount:
        response.request?.num_results_per_page ||
        constructorResponse.num_results_per_page ||
        this.resultsPerPage,
    })
    const facets = Array.isArray(constructorResponse.facets)
      ? constructorResponse.facets
        .map((facet) => this.mapFacet(facet))
        .filter(Boolean)
      : []

    return {
      request: response.request || null,
      requestMeta,
      products: {
        hits,
        facets,
        resultId: constructorResponse.result_id || response.result_id || null,
        total: constructorResponse.total_num_results || 0,
      },
    }
  }

  get client () {
    return window.theme?.constructorIo?.client || null
  }

  get tracker () {
    return this.client?.tracker || null
  }

  get activeFacets () {
    return this.getActiveFilterInputs()
      .filter((filter) => {
        return filter.type === 'list' && filter.field && filter.value
      })
      .reduce((activeFacets, filter) => {
        const existingFacet = activeFacets.find((facet) => {
          return facet.field === filter.field
        })

        if (!existingFacet) {
          activeFacets.push({
            field: filter.field,
            value: [filter.value],
          })

          return activeFacets
        }

        existingFacet.value.push(filter.value)
        return activeFacets
      }, [])
  }

  get emptyResponse () {
    return {
      request: null,
      products: {
        hits: [],
        facets: [],
        resultId: null,
        total: 0,
      },
    }
  }

  get stateLayer () {
    return window.theme.collectionBrowse.stateLayer
  }

  get collectionHandle () {
    return this.stateLayer?.state?.collectionHandle || ''
  }

  get currentPage () {
    return this.stateLayer?.state?.pagination?.currentPage || 1
  }

  get resultsPerPage () {
    return this.stateLayer?.state?.pagination?.size || 24
  }

  get sortBy () {
    return this.stateLayer?.state?.sortBy || ''
  }

  get sortOrder () {
    return this.stateLayer?.state?.sortOrder || ''
  }

  get pagination () {
    return this.stateLayer?.state?.pagination || {}
  }

  get searchParams () {
    return this.stateLayer?.state?.searchParams || new URLSearchParams()
  }
}

window.theme.collectionBrowse.apiLayer = new CollectionBrowseAPILayer()
